import { Flex } from '@common/components/Flex';
import { FormControl } from '@common/components/form-control';
import { DatasheetGridSettingsDto, DatasheetGridSettingsMarginType } from '@common/dto/datasheet/grid-settings-dto';
import { getTestAttributes } from '@common/utils/tests';
import { trDts } from '@modules/datasheets/utils/i18n';
import React from 'react';
import { Checkbox } from '@common/components/checkbox';
import { Radio } from '@common/components/radio';
import { Button } from '@common/components/Button';

import './grid-settings.styles.scss';

export type GridSettingsProps = {
  settings: DatasheetGridSettingsDto;
  onChange: (settings: DatasheetGridSettingsDto) => void;
  onResetTabOrder: () => void;
};

export const GridSettings: React.FC<GridSettingsProps> = (props) => {
  const { settings, onChange, onResetTabOrder } = props;
  const [settingsLocal, setSettingsLocal] = React.useState<DatasheetGridSettingsDto>(settings);

  React.useEffect(() => {
    setSettingsLocal(settings);
  }, [settings]);

  const handleChange = () => {
    onChange(settingsLocal);
  };

  const handleInputChange = (data: Partial<DatasheetGridSettingsDto>) => {
    const newSettings = {
      ...settingsLocal,
      ...data,
    };

    setSettingsLocal(newSettings);
    onChange(newSettings);
  };

  const handleSettingsLocalChange = (data: Partial<DatasheetGridSettingsDto>) => {
    const newSettings = {
      ...settingsLocal,
      ...data,
    };

    setSettingsLocal(newSettings);
  };

  const showCheckboxes = [
    {
      id: 'cbx-grid-operation-toolbar-menu-show-input',
      label: trDts('Show Grid'),
      checked: settingsLocal.showGrid,
      onChange: (checked: boolean) => handleInputChange({ showGrid: checked }),
    },
    {
      id: 'cbx-grid-operation-toolbar-menu-show-page-break-input',
      label: trDts('Show Page Breaks'),
      checked: settingsLocal.showPageBreaks,
      onChange: (checked: boolean) => handleInputChange({ showPageBreaks: checked }),
    },
    {
      id: 'cbx-grid-operation-toolbar-menu-snap-to-grid-input',
      label: trDts('Snap to Grid'),
      checked: settingsLocal.snapToGrid,
      onChange: (checked: boolean) => handleInputChange({ snapToGrid: checked }),
    },
    {
      id: 'cbx-grid-operation-toolbar-menu-show-tab-order-input',
      label: trDts('Show/Edit Tab Order'),
      checked: settingsLocal.showEditTabOrder,
      onChange: (checked: boolean) => handleInputChange({ showEditTabOrder: checked }),
      additionalComponent: (
        <Button 
          disabled={!settingsLocal.showEditTabOrder} 
          bsStyle='default' 
          onClick={onResetTabOrder}
          className="DatasheetsEditorHeaderGridSettings__reset-tab-order-button"
        >
          {trDts('Reset Tab Order')}
        </Button>
      ),
    },
  ];

  const gridControls = [
    {
      value: settingsLocal.horizontalGrid,
      id: 'tx-grid-operation-toolbar-menu-grid-x',
      label: trDts('Horizontal Grid'),
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleSettingsLocalChange({ horizontalGrid: Number(e.target.value) }),
    },
    {
      value: settingsLocal.verticalGrid,
      id: 'tx-grid-operation-toolbar-menu-grid-y',
      label: trDts('Vertical Grid'),
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleSettingsLocalChange({ verticalGrid: Number(e.target.value) }),
    },
  ];

  return (
    <div className="DatasheetsEditorHeaderGridSettings">
      {showCheckboxes.map((x) => (
        <React.Fragment key={x.label}>
          <Checkbox 
            {...getTestAttributes({ id: x.id })}
            checked={x.checked}
            className="DatasheetsEditorHeaderGridSettings__checkbox"
            label={x.label}
            onChange={x.onChange}
          />
          {x.additionalComponent}
        </React.Fragment>
      ))}

      <Checkbox
        className="DatasheetsEditorHeaderGridSettings__checkbox"
        checked={settingsLocal.showMargins}
        label={trDts('Show Margins')}
        onChange={(checked: boolean) => handleInputChange({ showMargins: checked })}
      />
      <div className="DatasheetsEditorHeaderGridSettings__margins-section">
        <Radio 
          className="DatasheetsEditorHeaderGridSettings__margins-section__radio"
          disabled={!settingsLocal.showMargins}
          {...getTestAttributes({ id: 'radio-grid-operation-toolbar-menu-fhq' })}
          label={trDts('FHQ')}
          checked={settingsLocal.marginType === DatasheetGridSettingsMarginType.fhq}
          onChange={() => handleInputChange({ marginType: DatasheetGridSettingsMarginType.fhq })}
        />
        <Radio 
          className="DatasheetsEditorHeaderGridSettings__margins-section__radio"
          disabled={!settingsLocal.showMargins}
          {...getTestAttributes({ id: 'radio-grid-operation-toolbar-menu-tablet-rb' })}
          label={trDts('Tablet')}
          checked={settingsLocal.marginType === DatasheetGridSettingsMarginType.tablet}
          onChange={() => handleInputChange({ marginType: DatasheetGridSettingsMarginType.tablet })}
        />
        <Flex alignItems="center">
          <Radio 
            className="DatasheetsEditorHeaderGridSettings__margins-section__radio"
            disabled={!settingsLocal.showMargins}
            {...getTestAttributes({ id: 'radio-grid-operation-toolbar-menu-custom-rb' })}
            label={trDts('Custom')}
            checked={settingsLocal.marginType === DatasheetGridSettingsMarginType.custom}
            onChange={() => handleInputChange({ marginType: DatasheetGridSettingsMarginType.custom })}
          />
          <FormControl
            {...getTestAttributes({ id: 'tx-grid-operation-toolbar-menu-custom-marginx' })}
            disabled={settingsLocal.marginType !== DatasheetGridSettingsMarginType.custom}
            type="number"
            className="DatasheetsEditorHeaderGridSettings__margins-section__input"
            value={settingsLocal.marginWidth}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSettingsLocalChange({ marginWidth: Number(e.target.value) })}
            onBlur={handleChange}
          />
          ✕
          <FormControl
            {...getTestAttributes({ id: 'tx-grid-operation-toolbar-menu-custom-marginy' })}
            disabled={settingsLocal.marginType !== DatasheetGridSettingsMarginType.custom}
            type="number"
            className="DatasheetsEditorHeaderGridSettings__margins-section__input"
            value={settingsLocal.marginHeight}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSettingsLocalChange({ marginHeight: Number(e.target.value) })}
            onBlur={handleChange}
          />
        </Flex>
      </div>

      {gridControls.map((x) => (
        <Flex key={x.label} alignItems="center" justify="space-between" className="DatasheetsEditorHeaderGridSettings__section">
          <span>{x.label}</span>
          <FormControl
            {...getTestAttributes({ id: x.id })}
            type="number"
            className="DatasheetsEditorHeaderGridSettings__grid-input"
            value={x.value}
            onChange={x.onChange}
            onBlur={handleChange}
          />
        </Flex>
      ))}
    </div>
  );
};
